import cn from 'classnames'
import { FC, ReactElement } from 'react'

/**
 * The icon-button surface, moved out of _btn.scss (P7).
 *
 * Literal strings per key: both call sites build their class with
 * `${variant}` / `${size}`, which Tailwind's scanner cannot resolve, so
 * utilities assembled the same way would be purged with no build error.
 *
 * The icon strokes repeat the variant class (and `disabled`) inside the arbitrary
 * variant, e.g. `[&.secondary_.icon-container_svg_path]`, to keep the 0-3-2 / 0-4-2
 * weight the SCSS rules had: a bare `[&_svg_path]:` is only 0-1-2 and would lose
 * to page-level icon strokes such as product-configuration's 0-2-2 chevron.
 */
export type IconButtonVariant = 'primary' | 'secondary' | 'text'
export type IconButtonSize = 'large' | 'medium' | 'small'

export const ICON_BUTTON_SIZES: Record<IconButtonSize, string> = {
  large: 'size-10',
  medium: 'size-8',
  small: 'size-6',
}

// The disabled secondary row carries only its 0-4-2 pair: it sets both properties
// above the 0-3-2 base pair's weight, so the base pair never showed on a disabled button.
export const ICON_BUTTON_VARIANTS: Record<IconButtonVariant, { base: string, disabled: string }> = {
  primary: {
    base: 'bg-primary-main [&.primary_.icon-container_svg_path]:stroke-gray-1 [&.primary_.icon-container_svg_path]:[stroke-opacity:1]',
    disabled: 'pointer-events-none bg-gray-4 [&.primary_.icon-container_svg_path]:stroke-gray-1 [&.primary_.icon-container_svg_path]:[stroke-opacity:1]',
  },
  secondary: {
    base: 'bg-gray-6 [&.secondary_.icon-container_svg_path]:stroke-gray-2 [&.secondary_.icon-container_svg_path]:[stroke-opacity:1]',
    disabled: 'pointer-events-none [&.secondary.disabled_.icon-container_svg_path]:stroke-gray-4 [&.secondary.disabled_.icon-container_svg_path]:[stroke-opacity:1]',
  },
  text: {
    base: 'bg-transparent [&.text_.icon-container_svg_path]:stroke-gray-1',
    disabled: '[&.text_.icon-container_svg_path]:stroke-gray-1',
  },
}

// `rounded-[50%]`, not `rounded-full`: the latter is 9999px, which paints the
// same circle but is not the same computed value.
//
// The icon margin is reset from the BUTTON: `[&_.icon-container]:ml-0` is a
// 0-2-0 descendant selector, so it beats the span's own 0-1-0 `ml-[8px]` below
// on specificity, whatever order the sheet emits them in.
export const ICON_BUTTON_BASE = 'rounded-[50%] p-0 [&_.icon-container]:ml-0'

type IconButtonProps = {
  icon: ReactElement,
  size: IconButtonSize,
  variant: IconButtonVariant,
  onClick: () => void,
  /**
   * Accessible name. An icon-only button has no text content, so without this it
   * reaches assistive tech — and role-based queries — as an unnamed button.
   */
  ariaLabel?: string,
  /** Set when the button discloses a region, so its state is announced. */
  ariaExpanded?: boolean,
  className?: string,
  isDisabled?: boolean,
  tabIndex?: number,
}

const IconButton:FC<IconButtonProps> = ({
  icon,
  size,
  variant,
  onClick,
  ariaLabel,
  ariaExpanded,
  className,
  isDisabled = false,
  tabIndex,
}) => {
  const buttonClassnames = cn(
    'icon-button',
    `${variant}`,
    `${size}`,
    ICON_BUTTON_BASE,
    ICON_BUTTON_SIZES[size],
    isDisabled ? ICON_BUTTON_VARIANTS[variant].disabled : ICON_BUTTON_VARIANTS[variant].base,
    { [`${className}`]: !!className },
    { disabled: isDisabled },
    // Arbitrary shadow property for the reason given in GrainButton.
    { 'pointer-events-none [box-shadow:none]': isDisabled },
  )

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      disabled={isDisabled}
      className={buttonClassnames}
      tabIndex={tabIndex}
    >
      <span className="icon-container flex items-center justify-center h-[16px] w-[16px] ml-[8px]">{ icon }</span>
    </button>
  )
}

export default IconButton
